import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Camera, User, Shield, Eye, EyeOff,
  GraduationCap, Globe, Brain, Save, Loader2,
  LogOut, Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { PageTitle } from '@/shared/components'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { updateCurrentUserApi } from '@/features/auth/api/auth.api'
import { useEtudiantByUserId, useUpdateEtudiant } from '@/features/etudiants/hooks/use-etudiants'
import type { Etudiant } from '@/shared/types/etudiant'
import type { User as AuthUser } from '@/shared/types/auth'
import { compressImage } from '@/lib/image'

// ── Helpers ───────────────────────────────────────────────

function getInitials(prenom: string, nom: string): string {
  return `${prenom?.[0] ?? ''}${nom?.[0] ?? ''}`.toUpperCase()
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  professeur: 'Formateur',
  etudiant: 'Étudiant',
  parent: 'Parent',
}

const MEMOIRE_LABELS: Record<string, string> = {
  KINESTHESIQUE: 'Kinesthésique',
  VISUELLE: 'Visuelle',
  AUDITIVE: 'Auditive',
}

// ── Sub-components ────────────────────────────────────────

function AvatarBlock({
  user,
  etudiant,
  size = 'lg',
  editable,
  previewUrl,
  photoRemoved,
  onPhotoSelect,
  onPhotoRemove,
}: {
  user: AuthUser
  etudiant?: Etudiant | null
  size?: 'lg' | 'xl'
  editable?: boolean
  previewUrl?: string | null
  photoRemoved?: boolean
  onPhotoSelect?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPhotoRemove?: () => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const photo = photoRemoved ? null : (previewUrl || etudiant?.photoUrl || user.photoUrl)
  const sizeClass = size === 'xl' ? 'h-32 w-32' : 'h-24 w-24'
  const textSize = size === 'xl' ? 'text-4xl' : 'text-2xl'
  const camSize = size === 'xl' ? 'h-10 w-10' : 'h-8 w-8'

  return (
    <div className="relative inline-block">
      <div
        className={cn(sizeClass, 'overflow-hidden rounded-full border-4 border-white shadow-lg', editable && 'cursor-pointer')}
        onClick={() => editable && fileRef.current?.click()}
      >
        {photo ? (
          <img src={photo} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className={cn('flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary/60 font-bold text-white', textSize)}>
            {getInitials(user.prenom, user.nom)}
          </div>
        )}
      </div>
      {editable && (
        <>
          <button
            onClick={() => fileRef.current?.click()}
            className={cn(camSize, 'absolute bottom-0 right-0 flex items-center justify-center rounded-full border-3 border-white bg-primary text-white shadow-md transition-transform hover:scale-105')}
          >
            <Camera className="h-4 w-4" />
          </button>
          {photo && onPhotoRemove && (
            <button
              onClick={onPhotoRemove}
              className="absolute top-0 right-0 flex h-7 w-7 items-center justify-center rounded-full border-2 border-white bg-red-500 text-white shadow-md transition-transform hover:scale-105"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" onChange={onPhotoSelect} className="hidden" />
        </>
      )}
    </div>
  )
}

function PasswordField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-neutral-700">{label}</label>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 pr-10 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25"
        />
        <button
          type="button"
          onClick={() => setShow(!show)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
        >
          {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
    </div>
  )
}

function ToggleRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={cn('relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors', checked ? 'bg-primary' : 'bg-neutral-200')}
      >
        <span className={cn('inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform', checked ? 'translate-x-6' : 'translate-x-1')} />
      </button>
      <span className="text-sm font-medium text-neutral-700">{label}</span>
    </label>
  )
}

// ── Types ────────────────────────────────────────────────

interface EditValues {
  prenom: string
  nom: string
  telephone: string
  nbMotsLangue: number
  typeMemoire: string
  consentementSilencieux: boolean
  consentementConsultation: boolean
  consentementModificationPlanning: boolean
}

// ── Main Page ────────────────────────────────────────────

export function ProfileDesktopPage() {
  const { user, logout } = useAuth()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { data: etudiant } = useEtudiantByUserId(
    user?.role === 'etudiant' ? user?.id : undefined,
  )

  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [photoFile, setPhotoFile] = useState<File | null>(null)
  const [photoRemoved, setPhotoRemoved] = useState(false)
  const [saved, setSaved] = useState(false)
  const updateMutation = useUpdateEtudiant()
  const isStudent = user?.role === 'etudiant' && !!etudiant

  const [editValues, setEditValues] = useState<EditValues>({
    prenom: user?.prenom || '',
    nom: user?.nom || '',
    telephone: user?.telephone || '',
    nbMotsLangue: 5,
    typeMemoire: '',
    consentementSilencieux: false,
    consentementConsultation: false,
    consentementModificationPlanning: false,
  })

  // Sync with fetched etudiant or user
  useEffect(() => {
    if (etudiant) {
      setEditValues({
        prenom: etudiant.prenom,
        nom: etudiant.nom,
        telephone: etudiant.telephone || '',
        nbMotsLangue: etudiant.nbMotsLangue,
        typeMemoire: etudiant.typeMemoire || '',
        consentementSilencieux: etudiant.consentementSilencieux,
        consentementConsultation: etudiant.consentementConsultation,
        consentementModificationPlanning: etudiant.consentementModificationPlanning,
      })
    } else if (user) {
      setEditValues((p) => ({
        ...p,
        prenom: user.prenom,
        nom: user.nom,
        telephone: user.telephone || '',
      }))
    }
  }, [etudiant, user])

  useEffect(() => {
    return () => { if (previewUrl) URL.revokeObjectURL(previewUrl) }
  }, [previewUrl])

  const hasChanges =
    editValues.prenom !== (etudiant?.prenom ?? user?.prenom ?? '') ||
    editValues.nom !== (etudiant?.nom ?? user?.nom ?? '') ||
    editValues.telephone !== (etudiant?.telephone ?? user?.telephone ?? '') ||
    (etudiant && (
      editValues.nbMotsLangue !== etudiant.nbMotsLangue ||
      editValues.typeMemoire !== (etudiant.typeMemoire || '') ||
      editValues.consentementSilencieux !== etudiant.consentementSilencieux ||
      editValues.consentementConsultation !== etudiant.consentementConsultation ||
      editValues.consentementModificationPlanning !== etudiant.consentementModificationPlanning
    )) ||
    photoFile !== null ||
    photoRemoved

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const compressed = await compressImage(file)
      setPhotoFile(compressed)
      setPhotoRemoved(false)
      if (previewUrl) URL.revokeObjectURL(previewUrl)
      setPreviewUrl(URL.createObjectURL(compressed))
    }
    e.target.value = ''
  }

  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    setSaving(true)
    try {
      if (etudiant) {
        // Étudiant: PUT /api/etudiants/:id
        const formData = new FormData()
        formData.append(
          'etudiant',
          new Blob([JSON.stringify(editValues)], { type: 'application/json' }),
        )
        if (photoFile) formData.append('file', photoFile)
        if (photoRemoved) formData.append('removePhoto', new Blob(['true'], { type: 'application/json' }))
        await updateMutation.mutateAsync({ id: etudiant.id, formData })
      } else {
        // Non-étudiant (admin, formateur): PUT /api/users/me
        const formData = new FormData()
        formData.append(
          'user',
          new Blob([JSON.stringify({
            prenom: editValues.prenom,
            nom: editValues.nom,
            telephone: editValues.telephone || null,
          })], { type: 'application/json' }),
        )
        if (photoFile) formData.append('file', photoFile)
        if (photoRemoved) formData.append('removePhoto', new Blob(['true'], { type: 'application/json' }))
        await updateCurrentUserApi(formData)
      }
      setPhotoFile(null)
      setPhotoRemoved(false)
      if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null) }
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch { /* handled by TanStack Query */ }
    setSaving(false)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const toggleLanguage = () => {
    const newLang = i18n.language.startsWith('fr') ? 'en' : 'fr'
    i18n.changeLanguage(newLang)
    localStorage.setItem('language', newLang)
  }

  if (!user) return null

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'
  const readOnlyClass =
    'h-10 w-full rounded-lg border border-neutral-200 bg-neutral-100 px-3 text-sm text-neutral-500 cursor-not-allowed'

  return (
    <div className="space-y-6 pb-12">
      <PageTitle
        icon={User}
        title="Profil"
        subtitle="Gérez vos informations personnelles"
      />
      <div className="flex gap-6">
      {/* ── Left column — Profile card ── */}
      <div className="w-72 shrink-0">
        <div className="sticky top-4 space-y-4">
          <div className="flex flex-col items-center rounded-xl border border-neutral-200 bg-white p-6 shadow-sm">
            <AvatarBlock user={user} etudiant={etudiant} size="xl" editable previewUrl={previewUrl} photoRemoved={photoRemoved} onPhotoSelect={handlePhotoSelect} onPhotoRemove={() => { setPhotoFile(null); setPhotoRemoved(true); if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null) } }} />
            <h2 className="mt-4 text-xl font-bold text-neutral-900">{user.prenom} {user.nom}</h2>
            <p className="text-sm text-neutral-500">{user.email}</p>
            <span className="mt-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
              {ROLE_LABELS[user.role] || user.role}
            </span>
            {isStudent && etudiant.parcours?.[0] && (
              <div className="mt-3 flex items-center gap-1.5 rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                <GraduationCap className="h-3.5 w-3.5 text-primary" />
                {etudiant.parcours[0].niveau}{etudiant.parcours[0].etablissement ? ` — ${etudiant.parcours[0].etablissement}` : ''}
              </div>
            )}
            {isStudent && etudiant.typeMemoire && (
              <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                <Brain className="h-3.5 w-3.5 text-primary" />
                Mémoire {MEMOIRE_LABELS[etudiant.typeMemoire] || etudiant.typeMemoire}
              </div>
            )}
          </div>

          {/* Language toggle */}
          <button
            onClick={toggleLanguage}
            className="flex w-full items-center justify-center gap-3 rounded-xl border border-neutral-200 bg-white py-3 text-sm font-medium text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50"
          >
            <Globe className="h-4 w-4 text-primary" />
            <span className="flex items-center gap-1.5">
              <span className={cn('transition-colors', i18n.language.startsWith('fr') ? 'font-bold text-primary' : 'text-neutral-400')}>FR</span>
              <span className="text-neutral-300">/</span>
              <span className={cn('transition-colors', !i18n.language.startsWith('fr') ? 'font-bold text-primary' : 'text-neutral-400')}>EN</span>
            </span>
          </button>

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-200 bg-white py-3 text-sm font-medium text-red-500 shadow-sm transition-colors hover:bg-red-50"
          >
            <LogOut className="h-4 w-4" />
            Se déconnecter
          </button>
        </div>
      </div>

      {/* ── Right column — Forms ── */}
      <div className="flex-1 space-y-6">
        {/* Personal info */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-100 px-6 py-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
              <User className="h-4 w-4 text-primary" />
              Informations personnelles
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Prénom</label>
                <input value={editValues.prenom} onChange={(e) => setEditValues((p) => ({ ...p, prenom: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Nom</label>
                <input value={editValues.nom} onChange={(e) => setEditValues((p) => ({ ...p, nom: e.target.value }))} className={inputClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Email</label>
                <input value={user.email} readOnly className={readOnlyClass} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-neutral-700">Téléphone</label>
                <input type="tel" value={editValues.telephone} onChange={(e) => setEditValues((p) => ({ ...p, telephone: e.target.value }))} placeholder="Non renseigné" className={inputClass} />
              </div>
            </div>
          </div>
        </div>

        {/* PEPS settings — students only */}
        {isStudent && (
          <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
            <div className="border-b border-neutral-100 px-6 py-4">
              <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
                <Brain className="h-4 w-4 text-primary" />
                Paramètres PEPS
              </h2>
            </div>
            <div className="space-y-5 p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-700">Mots par langue</label>
                  <div className="flex gap-2">
                    {[5, 10, 15].map((n) => (
                      <button
                        key={n}
                        onClick={() => setEditValues((p) => ({ ...p, nbMotsLangue: n }))}
                        className={cn(
                          'flex-1 rounded-lg py-2 text-sm font-bold transition-all',
                          editValues.nbMotsLangue === n
                            ? 'bg-primary text-white shadow-sm'
                            : 'border border-neutral-200 bg-neutral-50 text-neutral-600 hover:border-neutral-300',
                        )}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-neutral-700">Type de mémoire</label>
                  <select
                    value={editValues.typeMemoire}
                    onChange={(e) => setEditValues((p) => ({ ...p, typeMemoire: e.target.value }))}
                    className={inputClass}
                  >
                    <option value="">— Non renseigné —</option>
                    <option value="KINESTHESIQUE">Kinesthésique</option>
                    <option value="VISUELLE">Visuelle</option>
                    <option value="AUDITIVE">Auditive</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-neutral-100 pt-5">
                <h3 className="mb-3 text-sm font-semibold text-neutral-700">Consentements</h3>
                <div className="flex flex-col gap-3">
                  <ToggleRow label="Consentement Mode concentration" checked={editValues.consentementSilencieux} onChange={(v) => setEditValues((p) => ({ ...p, consentementSilencieux: v }))} />
                  <ToggleRow label="Consentement consultation" checked={editValues.consentementConsultation} onChange={(v) => setEditValues((p) => ({ ...p, consentementConsultation: v }))} />
                  <ToggleRow label="Consentement modification planning" checked={editValues.consentementModificationPlanning} onChange={(v) => setEditValues((p) => ({ ...p, consentementModificationPlanning: v }))} />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security */}
        <div className="rounded-xl border border-neutral-200 bg-white shadow-sm">
          <div className="border-b border-neutral-100 px-6 py-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-neutral-900">
              <Shield className="h-4 w-4 text-primary" />
              Sécurité
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-4 p-6">
            <PasswordField label="Nouveau mot de passe" value="" onChange={() => {}} placeholder="••••••••" />
            <PasswordField label="Confirmer le mot de passe" value="" onChange={() => {}} placeholder="••••••••" />
          </div>
        </div>

        {/* Save */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="flex items-center gap-2 rounded-lg bg-primary px-8 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : saved ? (
              <span className="text-emerald-200">Enregistré !</span>
            ) : (
              <Save className="h-4 w-4" />
            )}
            {!saved && (saving ? 'Enregistrement...' : 'Enregistrer')}
          </button>
        </div>
      </div>
      </div>
    </div>
  )
}
